/**
 * Text sanitization utilities for detecting and replacing
 * invalid/normalizable characters from PDF text selections.
 */

// Map of characters to their replacements
const normalizationMap = {
	// Dashes and minus
	"\u2010": "-",
	"\u2011": "-",
	"\u2012": "-",
	"\u2013": "-",
	"\u2014": "-",
	"\u2015": "-",
	"\u2212": "-",
	"\uFE58": "-",
	"\uFE63": "-",
	"\uFF0D": "-",
	// Superscripts
	"\u2070": "^0",
	"\u00b9": "^1",
	"\u00b2": "^2",
	"\u00b3": "^3",
	"\u2074": "^4",
	"\u2075": "^5",
	"\u2076": "^6",
	"\u2077": "^7",
	"\u2078": "^8",
	"\u2079": "^9",
	// Mathematical operators
	"\u00d7": "*",
	"\u00f7": "/",
	"\u2215": "/",
	"\u2217": "*",
	"\u2264": "<=",
	"\u2265": ">=",
	"\u00b1": "+/-",
	"\u2213": "-+",
	// Middle dot
	"\u00b7": ".",
	// Quotes
	"\u201c": '"',
	"\u201d": '"',
	"\u2018": "'",
	"\u2019": "'",
};

// Human-readable names for special characters
const charNames = {
	0x00b7: "middle dot (\u00b7)",
	0x00d7: "multiplication sign (\u00d7)",
	0x00f7: "division sign (\u00f7)",
	0x2010: "hyphen",
	0x2012: "figure dash",
	0x2013: "en dash (\u2013)",
	0x2014: "em dash (\u2014)",
	0x2212: "minus sign (\u2212)",
	0x2264: "less than or equal (\u2264)",
	0x2265: "greater than or equal (\u2265)",
	0x00b1: "plus-minus (\u00b1)",
	0x2213: "minus-or-plus",
	0x2070: "superscript 0 (\u2070)",
	0x00b9: "superscript 1 (\u00b9)",
	0x00b2: "superscript 2 (\u00b2)",
	0x00b3: "superscript 3 (\u00b3)",
	0x2074: "superscript 4 (\u2074)",
	0x2075: "superscript 5 (\u2075)",
	0x2076: "superscript 6 (\u2076)",
	0x2077: "superscript 7 (\u2077)",
	0x2078: "superscript 8 (\u2078)",
	0x2079: "superscript 9 (\u2079)",
	0x201c: "left double quote (\u201c)",
	0x201d: "right double quote (\u201d)",
	0x2018: "left single quote (\u2018)",
	0x2019: "right single quote (\u2019)",
};

/** Regex matching any character in the normalization map. */
const unicodeRegex = new RegExp(
	Object.keys(normalizationMap)
		.map((ch) => ch.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
		.join("|"),
	"g",
);

/**
 * Replace unicode characters with ASCII equivalents.
 * Returns the converted string if any substitutions were made, or null if unchanged.
 * Mirrors the backend's applyUnicodeConversion in QcMergeUtils.ts.
 * @param {string} value
 * @returns {string | null}
 */
export function applyUnicodeConversion(value) {
	if (!value) return null;
	unicodeRegex.lastIndex = 0;
	if (!unicodeRegex.test(value)) return null;
	unicodeRegex.lastIndex = 0;
	return value.replace(unicodeRegex, (ch) => normalizationMap[ch] ?? ch);
}

/**
 * Returns a human-readable name for a special character.
 * @param {string} char - Single character
 * @returns {string}
 */
export function getCharName(char) {
	const code = char.charCodeAt(0);

	// Control characters
	if (code < 0x20) {
		return "Invalid character";
	}

	return charNames[code] || `"${char}"`;
}

/**
 * Scans text for invalid or normalizable characters and returns
 * an array of replacement descriptors.
 * @param {string} text - Raw text to scan
 * @returns {Array<{type: string, originalChar: string, newValue: string, position: number, description: string, unicodeCode: string}>}
 */
export function findReplacements(text) {
	const replacements = [];

	for (let i = 0; i < text.length; i++) {
		const code = text.charCodeAt(i);
		// Resolve to a full code point so supplementary-plane chars (e.g.
		// Plane-15/16 Private Use) are classified, not their surrogate halves.
		let cp = code;
		let char = text[i];
		if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length) {
			const lo = text.charCodeAt(i + 1);
			if (lo >= 0xdc00 && lo <= 0xdfff) {
				cp = (code - 0xd800) * 0x400 + (lo - 0xdc00) + 0x10000;
				char = text[i] + text[i + 1];
			}
		}
		let type = null;
		let newValue = null;

		// These are almost always font-encoding artifacts - a glyph the PDF
		// couldn't map to real Unicode. The common math symbols are recovered
		// upstream (see pdfGlyphRecovery.js); whatever still reaches here is a
		// decimal point lost between digits, a genuine Unicode non-character, or
		// an unidentified dropped symbol the user should get to fix.
		const isLowControl =
			(cp >= 0x00 && cp <= 0x08) || (cp >= 0x0e && cp <= 0x1f);
		const isC1 = cp === 0x7f || (cp >= 0x80 && cp <= 0x9f);
		const isPUA =
			(cp >= 0xe000 && cp <= 0xf8ff) ||
			(cp >= 0xf0000 && cp <= 0xffffd) ||
			(cp >= 0x100000 && cp <= 0x10fffd);
		const isNonChar =
			cp === 0xfffe || cp === 0xffff || (cp >= 0xfdd0 && cp <= 0xfdef);
		const isReplacement = cp === 0xfffd;

		if (isLowControl) {
			// Check if it's between digits (smart decimal case)
			const prevChar = i > 0 ? text[i - 1] : "";
			const nextChar = i + 1 < text.length ? text[i + 1] : "";
			if (/\d/.test(prevChar) && /\d/.test(nextChar)) {
				type = "smart-decimal";
				newValue = "."; // Default to decimal, user can choose to remove
			} else {
				// Surface it as an editable choice instead of silently deleting
				// it - default stays removed so a straight "Apply Changes"
				// matches the old behavior.
				type = "unidentified";
				newValue = "";
			}
		}
		// Vertical tab and form feed
		else if (cp === 0x0b || cp === 0x0c) {
			type = "invalid";
			newValue = " ";
		}
		// Genuine Unicode non-characters - never valid content, just drop them.
		else if (isNonChar) {
			type = "invalid";
			newValue = "";
		}
		// Private-Use-Area, C1/DEL controls, and the replacement char are all
		// dropped/garbled glyphs that used to pass through silently. Surface
		// them editable too.
		else if (isPUA || isC1 || isReplacement) {
			type = "unidentified";
			newValue = "";
		}
		// Check normalization map
		else if (normalizationMap[char]) {
			type = "normalization";
			newValue = normalizationMap[char];
		}

		// If we found something to replace, add it to the list
		if (type) {
			replacements.push({
				type: type,
				originalChar: char,
				newValue: newValue,
				position: i,
				description: getCharName(char),
				unicodeCode: cp.toString(16).toUpperCase().padStart(4, "0"),
			});
		}

		// Skip the low surrogate we already consumed as part of a pair.
		if (char.length === 2) i++;
	}

	return replacements;
}
